/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

'use client';

import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Drawer from "@mui/material/Drawer";
import CloseIcon from "@mui/icons-material/Close";
import { IRouteInfo } from "../../globals/globals";

type NavItem = IRouteInfo;

export interface NavbarProps {
    className?: string;
    appName?: string;
    logo?: React.ReactNode;
    onSignOut?: () => void;
    onNavigate?: (path: string) => void;
    leftAdornment?: React.ReactNode;
    currentSection?: string;
    navItems?: NavItem[];
    children?: React.ReactNode;
    backgroundColor?: string;
}

const Navbar: React.FC<NavbarProps> = ({
    appName = 'AMS',
    logo,
    onSignOut,
    onNavigate,
    className,
    leftAdornment,
    currentSection,
    navItems = [],
    children,
    backgroundColor = 'var(--primary)',
}) => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState<string>(currentSection ?? '');

    useEffect(() => {
        if (currentSection) setActiveSection(currentSection);
    }, [currentSection]);

    const handleNavClick = (item: NavItem) => {
        setMobileMenuOpen(false);
        setActiveSection(item.Path ?? '');
        navigate(item.Path);
        onNavigate?.(item.Path);
    };

    const capitalizeText = (text: string) => {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    };

    const normalizedSection = activeSection.replace(/^\/+/, '').split('/')[0];
    const useRelativeToolbar = ['nodes', 'tasks', 'externalPerformance', 'administration'].includes(normalizedSection);

    return (
        <AppBar position="fixed" sx={{ backgroundColor, zIndex: 1100, borderBottom: '1px solid color-mix(in srgb, var(--primary-foreground) 20%, transparent)', boxShadow: 'none' }} className={cn('w-full', className)}>
            <Container maxWidth={false} sx={{ px: { xs: 2, md: 3, lg: 4 }, py: 0 }} disableGutters>
                <Toolbar disableGutters sx={{ position: useRelativeToolbar ? 'relative' : 'static', minHeight: { xs: 44, md: 56 }, maxHeight: { xs: 44, md: 56 }, py: 0, gap: { xs: 1, md: 2 }, px: { xs: 2, md: 3, lg: 4 } }}>
                    <div className="flex shrink-0 items-center gap-2 min-w-0 sm:gap-4">
                        {leftAdornment}
                        <div
                            className="flex shrink-0 items-center gap-4 text-primary-foreground min-w-0 sm:gap-6 navbar-logo-container"
                            style={{ textDecoration: 'none' }}
                        >
                            {logo && <div className="shrink-0">{logo}</div>}
                            <span className="hidden h-4 w-px sm:inline-block" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-foreground) 10%, transparent)' }} aria-hidden />
                            <span className="hidden text-lg font-bold tracking-tight sm:inline-block" style={{ color: 'var(--primary-foreground)', WebkitTextStroke: '0.5px currentColor' }}>
                                {appName}
                            </span>
                        </div>
                    </div>

                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            ...(useRelativeToolbar
                                ? {
                                      position: "absolute",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                  }
                                : {
                                      position: "static",
                                      flex: 1,
                                  }),
                            justifyContent: "center",
                            gap: { xs: 0.5, md: 1, lg: 1.5 },
                            flexWrap: "nowrap",
                            whiteSpace: "nowrap",
                            zIndex: 1,
                        }}
                    >
                        {navItems.map((item: NavItem) => {
                            const isSelected = activeSection === item.Path;
                            return (
                                <div
                                    key={item.Key}
                                    className={cn(
                                        'toolbar-nav-link',
                                        isSelected && 'toolbar-nav-link-active'
                                    )}
                                    onClick={() => handleNavClick(item)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            handleNavClick(item);
                                        }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {capitalizeText(item.Text)}
                                </div>
                            );
                        })}
                    </Box>

                    <div className="hidden md:flex items-center gap-3 ml-auto shrink-0">
                        {children}
                    </div>

                    <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" }, justifyContent: "flex-end" }}>
                        <IconButton
                            size="small"
                            aria-label="menu"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={() => setMobileMenuOpen(true)}
                            color="inherit"
                            sx={{ color: 'var(--primary-foreground)' }}
                        >
                            <MenuIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <Drawer
                            id="menu-appbar"
                            anchor="right"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            sx={{ display: { xs: "block", md: "none" }, '& .MuiDrawer-paper': { backgroundColor: 'var(--card)', color: 'var(--foreground)' } }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 280, sm: 320 },
                                    p: 3,
                                }}
                                role="presentation"
                            >

                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, justifyContent: "space-between" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        {logo && <div className="shrink-0">{logo}</div>}
                                        <span style={{ width: 1, height: 16, backgroundColor: "var(--border)" }} />
                                        <span style={{ fontWeight: "bold", color: "var(--foreground)" }}>{appName}</span>
                                    </Box>
                                    <IconButton
                                        onClick={() => setMobileMenuOpen(false)}
                                        sx={{ ml: "auto", color: 'var(--muted-foreground)', '&:hover': { backgroundColor: 'var(--accent)' } }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
                                    {navItems.map((item: NavItem) => {
                                        const isSelected = activeSection === item.Path;
                                        return (
                                            <div
                                                key={item.Key}
                                                className={cn(
                                                    'nav-menu-link',
                                                    isSelected && 'nav-menu-link-active'
                                                )}
                                                onClick={() => handleNavClick(item)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        handleNavClick(item);
                                                    }
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {capitalizeText(item.Text)}
                                            </div>
                                        );
                                    })}
                                </Box>

                                <Box sx={{ borderTop: 1, borderColor: "var(--border)", pt: 3 }}>
                                    {children}
                                </Box>
                            </Box>
                        </Drawer>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export { Navbar };
